import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, takeUntil, toArray } from 'rxjs/operators';
import { Partner, PartnerType } from '../../models/partner';
import {
  FeatureFlag, FeatureFlagDTO, FeatureFlagMatrixRow, featureFlagLabel, QuotaUnit, QUOTA_UNIT_LABELS
} from '../../models/tenant';
import { PartnersService } from '../../../services/partners.service';

/**
 * One page of partners is asked for. Well above any tenant count we have, and the page says so if
 * it is ever exceeded rather than quietly showing a subset.
 */
const TENANT_PAGE_SIZE = 500;

/** How many feature-flag calls may be in flight at once. The API is one call per tenant. */
const FLAG_REQUEST_CONCURRENCY = 6;

export interface FeatureFlagColumn {
  name: string;
  label: string;
  /** Tenants with this flag on, over all loaded rows — the footer figure. */
  enabled: number;
}

/**
 * Who has which feature: one row per tenant, one column per flag.
 *
 * <p>Read-only on purpose. Flags are switched on the partner page, which also owns the metered
 * limits; this page exists to answer "who has Accounting?" without opening every tenant. There is
 * no backend endpoint for the matrix, so it is assembled here from the partner list and one
 * feature-flags call per tenant.
 */
@Component({
  selector: 'app-feature-flags',
  templateUrl: './feature-flags.component.html',
  styleUrls: ['./feature-flags.component.scss']
})
export class FeatureFlagsComponent implements OnInit, OnDestroy {
  loading = true;
  refreshing = false;
  error: string | null = null;
  /** Set when the partner list had more tenants than one page holds. */
  truncatedBy = 0;
  failedRows = 0;

  rows: FeatureFlagMatrixRow[] = [];
  filteredRows: FeatureFlagMatrixRow[] = [];
  columns: FeatureFlagColumn[] = [];

  // Filters are pure array work against data already in memory — no request, so the search
  // input is never disabled or unmounted mid-keystroke.
  searchTerm = '';
  typeFilter: '' | PartnerType = '';
  featureFilter = '';

  readonly partnerTypes = Object.values(PartnerType);

  private destroy$ = new Subject<void>();

  constructor(private partnersService: PartnersService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.refreshing = true;
    this.error = null;

    this.partnersService.getPartners({ isTenant: true, page: 0, size: TENANT_PAGE_SIZE }).pipe(
      switchMap(page => {
        const tenants = (page.data || []).filter(p => p.id != null);
        this.truncatedBy = Math.max(0, (page.total || 0) - tenants.length);

        // One call per tenant; a failure marks that row rather than blanking the page.
        return from(tenants).pipe(
          mergeMap(tenant => this.partnersService.getTenantFeatureFlags(tenant.id!).pipe(
            map(flags => ({ tenant, flags: flags.items || [], failed: false })),
            catchError(err => {
              console.error(`Error loading feature flags for tenant ${tenant.id}:`, err);
              return of({ tenant, flags: [] as FeatureFlagDTO[], failed: true });
            })
          ), FLAG_REQUEST_CONCURRENCY),
          toArray()
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: results => {
        this.apply(results);
        this.loading = false;
        this.refreshing = false;
      },
      error: error => {
        console.error('Error loading tenants:', error);
        this.error = 'Failed to load tenants. Please try again.';
        this.loading = false;
        this.refreshing = false;
      }
    });
  }

  private apply(results: { tenant: Partner; flags: FeatureFlagDTO[]; failed: boolean }[]): void {
    this.rows = results
      .map(({ tenant, flags, failed }) => {
        const byName: Record<string, FeatureFlagDTO> = {};
        flags.forEach(flag => byName[flag.name] = flag);
        return {
          tenantId: tenant.id!,
          tenantName: tenant.contact?.name || `Tenant ${tenant.id}`,
          type: tenant.type,
          flags: byName,
          flagsFailed: failed,
          enabledCount: flags.filter(f => f.enabled).length
        };
      })
      .sort((a, b) => a.tenantName.localeCompare(b.tenantName));

    this.failedRows = this.rows.filter(r => r.flagsFailed).length;
    this.columns = this.buildColumns();
    this.applyFilters();
  }

  /**
   * Enum order first, then anything the backend returned that the hub has not been taught — a new
   * flag must show up here on the day it ships, not on the day someone edits tenant.ts.
   */
  private buildColumns(): FeatureFlagColumn[] {
    const names: string[] = Object.values(FeatureFlag);
    this.rows.forEach(row => Object.keys(row.flags).forEach(name => {
      if (!names.includes(name)) {
        names.push(name);
      }
    }));

    return names.map(name => ({
      name,
      label: featureFlagLabel(name),
      enabled: this.rows.filter(row => row.flags[name]?.enabled).length
    }));
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();

    this.filteredRows = this.rows.filter(row => {
      if (term && !row.tenantName.toLowerCase().includes(term)) {
        return false;
      }
      if (this.typeFilter && row.type !== this.typeFilter) {
        return false;
      }
      if (this.featureFilter && !row.flags[this.featureFilter]?.enabled) {
        return false;
      }
      return true;
    });
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.typeFilter = '';
    this.featureFilter = '';
    this.applyFilters();
  }

  isOn(row: FeatureFlagMatrixRow, column: FeatureFlagColumn): boolean {
    return !!row.flags[column.name]?.enabled;
  }

  /** "25 users" / "100 requests/month" for a metered flag that is on; empty otherwise. */
  limitLabel(row: FeatureFlagMatrixRow, column: FeatureFlagColumn): string {
    const flag = row.flags[column.name];
    if (!flag?.enabled || !flag.unit || flag.unit === QuotaUnit.NONE || flag.effectiveMonthlyLimit == null) {
      return '';
    }
    return `${flag.effectiveMonthlyLimit} ${QUOTA_UNIT_LABELS[flag.unit] ?? ''}`.trim();
  }

  limitTooltip(row: FeatureFlagMatrixRow, column: FeatureFlagColumn): string {
    const flag = row.flags[column.name];
    if (!flag?.enabled || !flag.unit || flag.unit === QuotaUnit.NONE) {
      return '';
    }
    return flag.monthlyLimit === null
      ? 'Free plan — no figure set for this tenant, so it takes the configured default.'
      : 'Negotiated figure set for this tenant.';
  }

  trackByRow(index: number, row: FeatureFlagMatrixRow): number {
    return row.tenantId;
  }

  trackByColumn(index: number, column: FeatureFlagColumn): string {
    return column.name;
  }
}
